import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Image, StyleSheet, Text, View } from 'react-native';
import Svg, { Defs, LinearGradient, Rect, Stop } from 'react-native-svg';

import { RewardChipPng } from '../../../assets/event';
import { useSvgId } from '../../../utils/hooks/useSvgId';
import { GoldGradientText } from '../common/GoldGradientText';
import EventPanel from './EventPanel';
import {
	EVENT_FONT,
	EVENT_FONT_CONDENSED,
	eventColors,
	GOLD_RAMP,
	groupThousands,
	PROGRESS_TRACK_FILL,
} from './eventTheme';

// Figma "Event points / 420 of 1200" (3324:18924): a 360x117 card, padding 12, 8 between rows.
export const POINTS_DESIGN_WIDTH = 360;
export const POINTS_DESIGN_HEIGHT = 117;

type Props = {
	points: number;
	goal: number;
	/** Points still needed for the next milestone; the caption is dropped once it is 0. */
	nextRewardIn: number;
	width: number;
};

export const EventPointsCard = ({ points, goal, nextRewardIn, width }: Props) => {
	const { t } = useTranslation();
	const scale = width / POINTS_DESIGN_WIDTH;
	const styles = useMemo(() => createStyles(scale), [scale]);
	const trackId = useSvgId('eventPointsTrack');
	const fillId = useSvgId('eventPointsFill');
	const filled = goal > 0 ? Math.min(points / goal, 1) : 0;

	return (
		<EventPanel
			radius={10 * scale}
			borderWidth={Math.max(1, 1 * scale)}
			style={{ width, height: POINTS_DESIGN_HEIGHT * scale }}
		>
			<View style={styles.summary}>
				<View style={styles.labels}>
					<Text numberOfLines={1} style={styles.caption}>
						{t('T_SITE_EVENT_CURRENT_POINTS', { defaultValue: 'YOUR CURRENT EVENT POINTS' })}
					</Text>
					<View style={styles.valueRow}>
						<GoldGradientText
							text={groupThousands(points)}
							width={90 * scale}
							height={38 * scale}
							fontSize={34 * scale}
							fontWeight='900'
							align='left'
						/>
						<Text numberOfLines={1} style={styles.goal}>
							{` / ${groupThousands(goal)}`}
						</Text>
					</View>
				</View>
				<Image source={RewardChipPng} style={styles.chip} resizeMode='contain' />
			</View>

			<View style={styles.track}>
				<Svg width='100%' height='100%'>
					<Defs>
						<LinearGradient id={trackId} x1='0' y1='0' x2='0' y2='1'>
							{PROGRESS_TRACK_FILL.map(([offset, color]) => (
								<Stop key={offset} offset={offset} stopColor={color} />
							))}
						</LinearGradient>
						<LinearGradient id={fillId} x1='0' y1='0' x2='0' y2='1'>
							{GOLD_RAMP.map(([offset, color]) => (
								<Stop key={offset} offset={offset} stopColor={color} />
							))}
						</LinearGradient>
					</Defs>
					<Rect x='0' y='0' width='100%' height='100%' fill={`url(#${trackId})`} />
					<Rect x='0' y='0' width={`${filled * 100}%`} height='100%' fill={`url(#${fillId})`} />
				</Svg>
			</View>

			{nextRewardIn > 0 ? (
				<Text numberOfLines={1} style={styles.hint}>
					{t('T_SITE_EVENT_POINTS_TO_NEXT_REWARD', {
						defaultValue: '{{points}} points to your next reward',
						points: groupThousands(nextRewardIn),
					})}
				</Text>
			) : null}
		</EventPanel>
	);
};

const createStyles = (scale: number) =>
	StyleSheet.create({
		summary: {
			position: 'absolute',
			left: 12 * scale,
			right: 12 * scale,
			top: 12 * scale,
			height: 55 * scale,
			flexDirection: 'row',
			alignItems: 'center',
		},
		labels: {
			flex: 1,
			gap: 4 * scale,
		},
		// Figma "Label/Micro": Sofia Sans Condensed Black 12, letter-spacing 0.48.
		caption: {
			color: eventColors.accent,
			fontFamily: EVENT_FONT_CONDENSED,
			fontSize: 12 * scale,
			fontWeight: '900',
			letterSpacing: 0.48 * scale,
			lineHeight: 15 * scale,
		},
		valueRow: {
			flexDirection: 'row',
			alignItems: 'center',
			gap: 6 * scale,
		},
		// Figma "Card/Meta": Sofia Sans Medium 12.
		goal: {
			color: eventColors.white,
			fontFamily: EVENT_FONT,
			fontSize: 12 * scale,
			fontWeight: '500',
		},
		chip: {
			width: 44 * scale,
			height: 44 * scale,
		},
		track: {
			position: 'absolute',
			left: 12 * scale,
			right: 12 * scale,
			top: 75 * scale,
			height: 8 * scale,
			borderRadius: 8 * scale,
			overflow: 'hidden',
		},
		hint: {
			position: 'absolute',
			left: 12 * scale,
			top: 91 * scale,
			color: eventColors.accent,
			fontFamily: EVENT_FONT,
			fontSize: 12 * scale,
			fontWeight: '500',
			lineHeight: 14 * scale,
		},
	});

export default EventPointsCard;
